'use client'

/**
 * The employee surfaces in the reader's language (locale audit 2026-09-26,
 * L-02 / L-13 / B02): the portal, the course player, /play, certificate
 * verification and accept-invite.
 *
 * Their strings live in messages/portal/<locale>.json; this module holds the
 * few things a plain t() call cannot do on its own:
 *
 *   - dates and numbers in the interface language, always in Western digits
 *     (the catalogues' rule too, lib/i18n/format-message.ts), and in English the
 *     en-US shapes these screens always printed ("Sep 30, 2026");
 *   - sentences with an element inside them (a bold name, a link);
 *   - labels for the enum values the API sends in English ('In Progress');
 *   - the portal copy every workspace is SEEDED with, which is not an admin's
 *     choice of words and so is shown in the reader's language.
 */

import { useMemo } from 'react'
import { useTranslation } from '@/lib/i18n'
import { parseApiDate } from '@/lib/time/parse-api-date'

type Translate = (key: string, params?: Record<string, string | number>) => string

// ---------------------------------------------------------------------------
// Dates and numbers
// ---------------------------------------------------------------------------

const INTL_LOCALES: Readonly<Record<string, string>> = {
  en: 'en-US',
  ar: 'ar-u-nu-latn',
  hi: 'hi-IN-u-nu-latn',
  fr: 'fr-FR',
}

/**
 * The Intl locale for an interface language: Western digits in every one. A
 * full tag ('en-GB') is used as given, for a screen that keeps its own English
 * shape.
 */
export function intlLocaleFor(locale: string): string {
  return INTL_LOCALES[locale] ?? (locale.includes('-') ? locale : INTL_LOCALES.en)
}

/** "Sep 30, 2026" / "30 سبتمبر 2026" / "30 सित॰ 2026" / "30 sept. 2026". */
export const DATE: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' }
/** DATE plus the time of day. */
export const DATE_TIME: Intl.DateTimeFormatOptions = { ...DATE, hour: 'numeric', minute: '2-digit' }

/**
 * A date in the reader's language; '' when there is none. Strings go through
 * parseApiDate, so a zone-less timestamp from the API is read as UTC.
 */
export function formatLearnerDate(
  value: string | number | Date | null | undefined,
  locale: string,
  options: Intl.DateTimeFormatOptions = DATE,
): string {
  const date = parseApiDate(value)
  if (!date) return ''
  try {
    return new Intl.DateTimeFormat(intlLocaleFor(locale), options).format(date)
  } catch {
    return date.toISOString().slice(0, 10)
  }
}

const DAY_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * A calendar day ('2026-09-30') in the reader's language: "Sep 30, 2026" /
 * "30 سبتمبر 2026". A due date arrives as the workspace's day, already decided,
 * so it is formatted as that day with no zone left to move it. A value that is
 * not a day (a hand-written preview's "Dec 12, 2026") is shown as given.
 */
export function formatLearnerDay(
  value: string | null | undefined,
  locale: string,
  options: Intl.DateTimeFormatOptions = DATE,
): string {
  const text = (value ?? '').trim()
  const m = DAY_ONLY.exec(text)
  if (!m) return text
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  // '2026-02-30' is not a day.
  if (date.toISOString().slice(0, 10) !== text) return text
  try {
    return new Intl.DateTimeFormat(intlLocaleFor(locale), { ...options, timeZone: 'UTC' }).format(date)
  } catch {
    return text
  }
}

/** A number grouped the reader's way (1,234 · 1,23,456 in Hindi), Western digits. */
export function formatLearnerNumber(value: number, locale: string): string {
  try {
    return new Intl.NumberFormat(intlLocaleFor(locale)).format(value)
  } catch {
    return String(value)
  }
}

/** The formatters bound to the current interface language. */
export function useLearnerFormat() {
  const { locale } = useTranslation()
  return useMemo(
    () => ({
      locale,
      date: (value: string | number | Date | null | undefined, options?: Intl.DateTimeFormatOptions) =>
        formatLearnerDate(value, locale, options),
      /** A calendar day ('2026-09-30'), e.g. a due date — see formatLearnerDay. */
      day: (value: string | null | undefined) => formatLearnerDay(value, locale),
      dateTime: (value: string | number | Date | null | undefined) => formatLearnerDate(value, locale, DATE_TIME),
      number: (value: number) => formatLearnerNumber(value, locale),
    }),
    [locale],
  )
}

// ---------------------------------------------------------------------------
// Sentences with elements in them
// ---------------------------------------------------------------------------

/**
 * Render a catalogue sentence whose {placeholders} are elements, not strings:
 * `richText(t('acceptInvite.mismatch.sentTo'), { email: <b>{email}</b> })`.
 * The app's one copy, lib/i18n/rich-text.tsx (the same contract this file's
 * own copy had); re-exported so the employee surfaces keep importing it here.
 */
export { richText } from '@/lib/i18n/rich-text'

// ---------------------------------------------------------------------------
// Values the API sends in English
// ---------------------------------------------------------------------------

/** An enrolment status ('In Progress'); a value this list does not know is shown as sent. */
export function enrollmentStatusLabel(t: Translate, status: string | null | undefined): string {
  switch (status) {
    case 'Scheduled':
      return t('portal.status.scheduled')
    case 'Not Started':
      return t('portal.status.notStarted')
    case 'In Progress':
      return t('portal.status.inProgress')
    case 'Completed':
      return t('portal.status.completed')
    case 'Overdue':
      return t('portal.status.overdue')
    case 'Failed':
      return t('portal.status.failed')
    case 'Cancelled':
      return t('portal.status.cancelled')
    case 'Passed':
      return t('portal.status.passed')
    default:
      return status ?? ''
  }
}

/** A coarse risk band ('High' | 'Medium' | 'Low'). */
export function riskLevelLabel(t: Translate, level: string | null | undefined): string {
  switch (level) {
    case 'High':
      return t('portal.home.risk.high')
    case 'Medium':
      return t('portal.home.risk.medium')
    case 'Low':
      return t('portal.home.risk.low')
    default:
      return level ?? ''
  }
}

// ---------------------------------------------------------------------------
// Seeded portal copy
// ---------------------------------------------------------------------------

export type PortalCopyField = 'heroHeading' | 'heroDescription' | 'ctaButtonText'

/**
 * The portal copy as the reader should see it.
 *
 * Every workspace is provisioned with the same English hero text (the API's
 * SYSTEM_DEFAULTS and default-portal-settings.json, and the web's own
 * fallbacks). A stored value that is still exactly that text is the SEED, not
 * wording an admin chose ("seeded is not configured", CLAUDE.md), so it is
 * shown in the reader's language. Anything else is the admin's own text and is
 * shown exactly as written.
 */
export function portalCopy(t: Translate, field: PortalCopyField, value: string | null | undefined): string {
  const text = (value ?? '').trim()
  switch (field) {
    case 'heroHeading':
      if (text === 'Welcome to Security Portal') return t('portal.copy.heroHeading')
      break
    case 'heroDescription':
      if (text === 'Stay informed and protected with our security awareness platform.')
        return t('portal.copy.heroDescription')
      if (text === 'Stay informed and protected') return t('portal.copy.heroDescriptionShort')
      break
    case 'ctaButtonText':
      if (text === 'EXPLORE COURSES') return t('portal.copy.cta')
      break
  }
  return value ?? ''
}

// ---------------------------------------------------------------------------
// Relative time
// ---------------------------------------------------------------------------

/** "5 min ago", "2 hours ago", … for the portal's points history. */
export function portalTimeAgo(t: Translate, value: string | number | Date | null | undefined, now = Date.now()): string {
  const date = parseApiDate(value)
  if (!date) return typeof value === 'string' ? value : ''
  const mins = Math.floor((now - date.getTime()) / 60000)
  if (mins < 60) return t('portal.time.minutesAgo', { count: mins })
  const hours = Math.floor(mins / 60)
  if (hours < 24) return t('portal.time.hoursAgo', { count: hours })
  const days = Math.floor(hours / 24)
  if (days < 7) return t('portal.time.daysAgo', { count: days })
  const weeks = Math.floor(days / 7)
  if (weeks < 5) return t('portal.time.weeksAgo', { count: weeks })
  return t('portal.time.monthsAgo', { count: Math.floor(days / 30) })
}
